import React, { useEffect, useState, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { validatePincodeByCountry } from './helper';
import pincodeService from './PincodeService';
import "./styles.css";
import { INPUT_AUTOFILL_PROPS } from '../../../../components/InputField/autofillProps';
import PreviousValue from '../../../../components/InputField/PreviousValue';
import { buildAutomationChildTestId, resolveAutomationTestId } from '../../../../components/InputField/testId';

function PincodeField(props) {
    const {
        id,
        countryCode,
        value: initialValue,
        onChange,
        label = "Pincode",
        isDisabled,
        required,
        className,
        showErr,
        prevValue,
        onError = () => { },
        onCommit,
        ...rest
    } = props;

    const [value, setValue] = useState("");
    const [error, setError] = useState(null);
    const [hasError, setHasError] = useState(false);
    const inputRef = useRef(null);
    const selectionRef = useRef({ start: 0, end: 0 });
    const fieldTestId = resolveAutomationTestId(props?.["data-testid"], id, label);

    // Update the field value if the initial value changes.
    useEffect(() => {
        const newValue = initialValue !== undefined && initialValue !== null
            ? String(initialValue).toUpperCase()
            : "";
        setValue(newValue);
    }, [initialValue]);

    // Cursor position preservation
    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.setSelectionRange(
                selectionRef.current.start,
                selectionRef.current.end
            );
        }
    }, [value]);

    // Trigger fetching of pincode data (if not already loaded).
    useEffect(() => {
        if (Object.keys(pincodeService.records).length === 0) {
            pincodeService.fetch().catch(console.error);
        }
    }, []);

    // Handle change in the input.
    const handleChange = (newValue) => {
        setValue(newValue);
        if (onChange) {
            onChange(newValue);
        }
        // Reset any previous error.
        setError(null);
        onError(null);
        setHasError(false);
    };

    // Validate the pincode.
    // If shouldNotEmpty is true, then an empty value will produce an error.
    const validatePincode = (shouldNotEmpty) => {
        if (!value) {
            if (shouldNotEmpty) {
                setError("Please enter a valid zip code");
                onError("Please enter a valid zip code");
                setHasError(true);
            } else {
                setError(null);
                onError(null);
                setHasError(false);
            }
            return;
        }
        const isValid = validatePincodeByCountry(countryCode, value);
        if (!isValid) {
            setError("Please enter a valid zip code");
            setHasError(true);
            onError("Please enter a valid zip code");
        } else {
            setError(null);
            setHasError(false);
            onError(null);
        }
    };

    // Re-derive on every value/country/showErr change (and on mount) so an empty required zip keeps
    // its red highlight across re-renders, not only when showErr first flips true.
    useEffect(() => {
        validatePincode(showErr);
    }, [countryCode, value, showErr]);

    return (
        <div className={`${className ? className : ''}`} data-testid={fieldTestId}>
            <div className="inputfield__container">
                <div className="inputfield__innercontainer_outline">
                    <div className="inputfield ps-0 " >
                        <input
                            ref={inputRef}
                            id={id}
                            data-testid={buildAutomationChildTestId(fieldTestId, "input")}
                            {...INPUT_AUTOFILL_PROPS}
                            className={`inputfield__input ${hasError ? 'error' : ''} ${isDisabled ? "cursor-disabled" : ""}`}
                            value={value}
                            onChange={(e) => {
                                const newValue = e.target.value.toUpperCase();
                                const start = e.target.selectionStart;
                                const end = e.target.selectionEnd;
                                selectionRef.current = { start, end };
                                handleChange(newValue);
                            }}
                            onBlur={() => {
                                // Pass showErr so blurring an empty required zip the gate already
                                // flagged keeps its error — not clears it (no-arg = not-required).
                                validatePincode(showErr);
                                onCommit?.(value);
                            }}
                            disabled={isDisabled}
                            {...rest}
                            style={{
                                height: "50px",
                                borderRadius: "15px",
                                paddingLeft: "15px",
                                border: "none",
                                fontSize: "14px",
                                outline: "none",
                                backgroundColor: prevValue?.has ? "#FFF9D6" : isDisabled ? "#F6F6F6" : "#F1FAFC",
                                width: "100%"
                            }}
                        />
                    </div>
                    <em className="inputfield__desc pt-0 d-block" data-testid={buildAutomationChildTestId(fieldTestId, "description")}>
                        {"E.g. 80203"}
                    </em>
                </div>

                {/* The error marker lives INSIDE inputfield__container (not as a sibling of it) so the
                    V2 `.inputfield__container:has(.inputfield__error)` rule can turn the field outline
                    red on error — the same red-border treatment every other field gets. Previously it
                    sat outside the container, so an invalid zip showed the message but no red border. */}
                {error && (hasError || showErr) && (
                    <em className="inputfield__error" data-testid={buildAutomationChildTestId(fieldTestId, "error")}>
                        {error}
                    </em>
                )}
            </div>

            <PreviousValue
                show={prevValue?.has}
                value={prevValue?.value}
                testId={buildAutomationChildTestId(fieldTestId, "previous")}
            />
        </div>
    );
}

export default observer(PincodeField);
